import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
    pairStructuresByName,
    StructurePairing,
} from '~/domain/dose/dvhComparison';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Dvh } from '~/domain/patient/dvh';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { replanningCompareViewState } from '~/state/atoms/adaptiveReplanningAtoms';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useReviewDatasets } from './useReviewDatasets';
import { logErrorName } from '~/utils/logErrorName';

export type DvhComparisonAvailability =
    | 'ready'
    | 'noPriorDose'
    | 'noCurrentDose'
    | 'needsComparison'
    | 'referenceRowNotLoaded';

export interface DvhComparisonEntry {
    readonly name: string;
    readonly color: DicomColor;
    // Undefined when the structure has no volume to histogram.
    readonly prior?: Dvh;
    readonly current?: Dvh;
}

export interface DvhComparison {
    readonly availability: DvhComparisonAvailability;
    readonly pairing: StructurePairing;
    readonly priorDose?: DoseMatrix;
    readonly currentDose?: DoseMatrix;
    readonly entries: readonly DvhComparisonEntry[];
    readonly loading: boolean;
    readonly failed: boolean;
}

const EMPTY_PAIRING: StructurePairing = {
    pairs: [],
    onlyPrior: [],
    onlyCurrent: [],
};

const NEUTRAL: DicomColor = { R: 200, G: 200, B: 200 };

const nextFrame = () =>
    new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

async function dvhOf(
    dose: DoseMatrix,
    roi: RegionOfInterest | undefined
): Promise<Dvh | undefined> {
    // Reading the texture builds it; a structure never drawn would otherwise histogram as empty.
    if (!roi || !roi.texture || roi.isEmpty) {
        return undefined;
    }
    return dose.getCalculatedDvh(roi);
}

// Prior: the prior dose on the planning structures. Current: the dose on the target, on the structures drawn with it.
export function useDvhComparison(
    selectedNames: readonly string[],
    enabled: boolean
): DvhComparison {
    const { sourceSet, priorDose, propagatedSet } = useReviewDatasets();
    const shownDose = useCurrentDose();
    const comparing = useRecoilValue(replanningCompareViewState);
    const targetSeriesUid = propagatedSet?.image?.seriesUid;
    const currentDose =
        shownDose &&
        targetSeriesUid &&
        shownDose.structureSet?.image?.seriesUid === targetSeriesUid
            ? shownDose
            : undefined;
    const currentSet = currentDose?.structureSet ?? undefined;

    const pairing = useMemo(
        () =>
            sourceSet && currentSet
                ? pairStructuresByName(sourceSet.roiArray, currentSet.roiArray)
                : EMPTY_PAIRING,
        [sourceSet, currentSet]
    );

    const [notLoaded, setNotLoaded] = useState(false);
    const expected: DvhComparisonAvailability = !priorDose
        ? 'noPriorDose'
        : !currentDose
          ? 'noCurrentDose'
          : !comparing
            ? 'needsComparison'
            : 'ready';
    const availability: DvhComparisonAvailability =
        expected === 'ready' && notLoaded ? 'referenceRowNotLoaded' : expected;

    const [entries, setEntries] = useState<readonly DvhComparisonEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    // Keyed by content: callers rebuild the list every render.
    const selectedKey = selectedNames.join('\u0000');
    const selected = useMemo(
        () => (selectedKey ? selectedKey.split('\u0000') : []),
        [selectedKey]
    );

    useEffect(() => {
        setEntries([]);
        setFailed(false);
        setNotLoaded(false);
        if (
            !enabled ||
            expected !== 'ready' ||
            !priorDose ||
            !currentDose ||
            !sourceSet ||
            !currentSet
        ) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        void (async () => {
            try {
                // The comparison fills the reference row in its own effect, after this one.
                await nextFrame();
                const registry = getDatasetEnvironmentRegistry();
                const primary = currentSet.image
                    ? registry.environmentFor(currentSet.image.seriesUid)
                          ?.structureSet
                    : undefined;
                const secondary = sourceSet.image
                    ? registry.environmentFor(sourceSet.image.seriesUid)
                          ?.structureSet
                    : undefined;
                const currentRois =
                    primary?.parent === currentSet ? primary : undefined;
                const priorRois =
                    secondary?.parent === sourceSet ? secondary : undefined;
                // Half a comparison reads as complete: compute nothing unless both rows hold their sets.
                if (!currentRois || !priorRois) {
                    if (!cancelled) {
                        setNotLoaded(true);
                    }
                    return;
                }
                const chosen = pairing.pairs.filter((pair) =>
                    selected.includes(pair.name)
                );
                const computed: DvhComparisonEntry[] = [];
                for (const pair of chosen) {
                    if (cancelled) {
                        return;
                    }
                    const currentRoi = currentRois.getRoi(
                        pair.currentRoiNumber
                    );
                    const priorRoi = priorRois.getRoi(pair.priorRoiNumber);
                    computed.push({
                        name: pair.name,
                        color: (currentRoi ?? priorRoi)?.colorRgb8 ?? NEUTRAL,
                        prior: await dvhOf(priorDose, priorRoi),
                        current: await dvhOf(currentDose, currentRoi),
                    });
                    await nextFrame();
                }
                if (!cancelled) {
                    setEntries(computed);
                }
            } catch (error) {
                logErrorName('DVH comparison failed:', error);
                if (!cancelled) {
                    setFailed(true);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        enabled,
        expected,
        priorDose,
        currentDose,
        sourceSet,
        currentSet,
        pairing,
        selected,
    ]);

    return {
        availability,
        pairing,
        priorDose,
        currentDose,
        entries,
        loading,
        failed,
    };
}
